import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadIcon from '@mui/icons-material/Upload';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { useMemo, useState, type ComponentType, type ReactNode } from 'react';

import type { ApiItemType } from '@highimpact-frontend/api-types';
import { PermissionDecorator } from '@highimpact/react-decorators';
import { useDashboardPermissionScope } from '../../../hooks/useDashboardPermissionScope';
import {
  useWidgetInstanceRegistry,
  useWidgetInstanceRegistryVersion,
} from '../../../runtime/instanceRegistry/WidgetInstanceRegistryProvider';
import type {
  WidgetToolbarAction,
  WidgetToolbarBuiltinIcon,
  WidgetToolbarIcon,
} from '../../../runtime/instanceRegistry/types';

// -- type definitions --

/** Temporary prop until implementation of WidgetTypeOperationsRegistry. */
export interface WidgetPlatformToolbarActionConfig {
  readonly visible: boolean;
  readonly tooltipText: string;
  readonly onClick: () => unknown;
  readonly permissionItem: ApiItemType;
  readonly buttonIcon?: WidgetToolbarIcon;
  readonly order?: number;
  readonly disabled?: boolean;
}

/** Temporary prop until implementation of WidgetTypeOperationsRegistry. */
export interface WidgetPlatformToolbarActionsConfig {
  readonly edit?: WidgetPlatformToolbarActionConfig;
  readonly delete?: WidgetPlatformToolbarActionConfig;
}

export interface WidgetToolbarProps {
  readonly dashboardElementId: number;
  readonly dashboardId?: number;
  /** @deprecated Will be replaced by type-registry `platformToolbarActions`. */
  readonly platformActions?: WidgetPlatformToolbarActionsConfig;
  /** Rendered after instance actions, before platform actions, (mostly legacy actions). */
  readonly children?: ReactNode;
}

interface ResolvedToolbarAction {
  readonly key: string;
  readonly buttonIcon: WidgetToolbarIcon;
  readonly tooltipText: string;
  readonly performAction: () => unknown;
  readonly order: number;
  readonly disabled?: boolean;
  readonly permissionItem?: ApiItemType;
}

// -- constants --

const BUILTIN_ICONS: Record<
  WidgetToolbarBuiltinIcon,
  ComponentType<{ className?: string }>
> = {
  'chevron-down': KeyboardArrowDownIcon,
  download: DownloadIcon,
  upload: UploadIcon,
  fullscreen: FullscreenIcon,
  refresh: RefreshIcon,
  'more-vertical': MoreVertIcon,
  edit: EditIcon,
  delete: DeleteOutlineIcon,
};

const PLATFORM_EDIT_ORDER = 900;
const PLATFORM_DELETE_ORDER = 910;

// -- helper functions --

function resolveToolbarIcon(icon: WidgetToolbarIcon) {
  if (typeof icon === 'string') {
    return BUILTIN_ICONS[icon] ?? ChevronRightIcon;
  }
  return icon;
}

function toResolvedInstanceAction(
  action: WidgetToolbarAction
): ResolvedToolbarAction {
  return {
    key: action.id,
    buttonIcon: action.buttonIcon,
    tooltipText: action.tooltipText,
    performAction: action.performAction,
    order: action.order ?? 0,
    disabled: action.disabled,
    permissionItem: action.permissionItem,
  };
}

function toResolvedPlatformAction(
  id: string,
  config: WidgetPlatformToolbarActionConfig,
  defaultIcon: WidgetToolbarBuiltinIcon,
  defaultOrder: number
): ResolvedToolbarAction {
  return {
    key: id,
    buttonIcon: config.buttonIcon ?? defaultIcon,
    tooltipText: config.tooltipText,
    performAction: config.onClick,
    order: config.order ?? defaultOrder,
    disabled: config.disabled,
    permissionItem: config.permissionItem,
  };
}

function resolvePlatformActions(
  platformActions?: WidgetPlatformToolbarActionsConfig
): ResolvedToolbarAction[] {
  if (!platformActions) {
    return [];
  }

  const resolved: ResolvedToolbarAction[] = [];

  if (platformActions.edit?.visible) {
    resolved.push(
      toResolvedPlatformAction(
        'platform-edit',
        platformActions.edit,
        'edit',
        PLATFORM_EDIT_ORDER
      )
    );
  }

  if (platformActions.delete?.visible) {
    resolved.push(
      toResolvedPlatformAction(
        'platform-delete',
        platformActions.delete,
        'delete',
        PLATFORM_DELETE_ORDER
      )
    );
  }

  return resolved;
}

// -- components --

export function WidgetToolbarIconButton({
  buttonIcon,
  tooltipText,
  disabled,
  permissionItem,
  dashboardId,
  onClick,
}: {
  readonly buttonIcon: WidgetToolbarIcon;
  readonly tooltipText: string;
  readonly disabled?: boolean;
  readonly permissionItem?: ApiItemType;
  readonly dashboardId?: number;
  readonly onClick: () => unknown;
}) {
  const { site_id, project_id } = useDashboardPermissionScope();
  const [pending, setPending] = useState(false);
  const Icon = resolveToolbarIcon(buttonIcon);

  const button = (
    <IconButton
      size="small"
      aria-label={tooltipText}
      disabled={disabled || pending}
      onClick={async () => {
        setPending(true);
        try {
          await onClick();
        } finally {
          setPending(false);
        }
      }}
    >
      <Icon className="widget-toolbar-icon" />
    </IconButton>
  );

  const wrapped = permissionItem ? (
    <PermissionDecorator
      item={permissionItem}
      project_id={project_id}
      site_id={site_id}
      extra={
        dashboardId !== undefined ? { dashboard_id: dashboardId } : undefined
      }
    >
      {button}
    </PermissionDecorator>
  ) : (
    button
  );

  return (
    <Tooltip title={tooltipText}>
      <span>{wrapped}</span>
    </Tooltip>
  );
}

function ToolbarActionButton({
  action,
  dashboardId,
}: {
  action: ResolvedToolbarAction;
  dashboardId?: number;
}) {
  return (
    <WidgetToolbarIconButton
      buttonIcon={action.buttonIcon}
      tooltipText={action.tooltipText}
      disabled={action.disabled}
      permissionItem={action.permissionItem}
      dashboardId={dashboardId}
      onClick={action.performAction}
    />
  );
}

// -- main component --

// TODO: get platform actions from WidgetTypeOperationsRegistry
/**
 * Toolbar displayed at the top of a widget.
 *
 * Renders instance-scoped actions from the widget registry and,
 * optional platform actions (configure/delete) passed by the nav
 * shell.
 */
export function WidgetToolbar({
  dashboardElementId,
  dashboardId,
  platformActions,
  children,
}: WidgetToolbarProps) {
  const registry = useWidgetInstanceRegistry();
  const version = useWidgetInstanceRegistryVersion();
  const { instanceActions, platformResolved } = useMemo(() => {
    const instance = registry.getInstance(dashboardElementId);
    const instanceActions = (instance?.toolbarActions ?? [])
      .map(toResolvedInstanceAction)
      .sort((a, b) => a.order - b.order);
    const platformResolved = resolvePlatformActions(platformActions).sort(
      (a, b) => a.order - b.order
    );

    return { instanceActions, platformResolved };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardElementId, platformActions, registry, version]);

  if (
    instanceActions.length === 0 &&
    platformResolved.length === 0 &&
    !children
  ) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={0.25}
      alignItems="center"
      className="widget-toolbar"
    >
      {instanceActions.map((action) => (
        <ToolbarActionButton
          key={action.key}
          action={action}
          dashboardId={dashboardId}
        />
      ))}
      {children}
      {platformResolved.map((action) => (
        <ToolbarActionButton
          key={action.key}
          action={action}
          dashboardId={dashboardId}
        />
      ))}
    </Stack>
  );
}
